import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { referralAPI } from '../api/endpoints';
import { formatXPUNT, formatDate, copyToClipboard } from '../utils/helpers';
import {
  FaArrowLeft, FaUsers, FaGift, FaCopy, FaCheck,
  FaTwitter, FaWhatsapp, FaTelegram, FaFacebook, FaEnvelope,
  FaPercentage, FaWallet, FaHistory,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './ReferralDashboard.css';

// ================================================================
// Defaults — used whenever the API omits a field
// ================================================================
const EMPTY_DATA = {
  referral_code: '',
  referral_link: '',
  total_referrals: 0,
  referral_earnings: 0,
  referred_users: [],
  referral_history: [],
};

// Normalize whatever the API sends us into the shape this component expects.
const normalizeResponse = (raw) => {
  if (!raw || typeof raw !== 'object') return EMPTY_DATA;

  // Handle nesting: { data: {...} } vs flat { ... }
  const payload =
    raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  return {
    referral_code:
      payload.referral_code ||
      payload.code ||
      payload.user_referral_code ||
      '',
    referral_link:
      payload.referral_link ||
      payload.link ||
      payload.referral_url ||
      '',
    total_referrals: Number(payload.total_referrals || payload.referrals_count || 0),
    referral_earnings: Number(
      payload.referral_earnings ||
      payload.earnings ||
      payload.total_earnings ||
      0
    ),
    referred_users: Array.isArray(payload.referred_users)
      ? payload.referred_users
      : Array.isArray(payload.users)
        ? payload.users
        : [],
    referral_history: Array.isArray(payload.referral_history)
      ? payload.referral_history
      : Array.isArray(payload.history)
        ? payload.history
        : [],
  };
};

// ================================================================
// COMPONENT
// ================================================================
const ReferralDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [referralData, setReferralData] = useState(EMPTY_DATA);
  const [copied, setCopied] = useState(false);

  // ==================== FETCH ====================
  const fetchReferralData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await referralAPI.getStats();
      const normalized = normalizeResponse(response?.data);
      setReferralData(normalized);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
      toast.error('Failed to load referral data');
      setReferralData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchReferralData();
  }, [isAuthenticated, navigate, fetchReferralData]);

  // ==================== DERIVED VALUES (all safe) ====================
  const fallbackCode = referralData.referral_code || user?.id || '';
  const referralLink =
    referralData.referral_link ||
    (fallbackCode ? `${window.location.origin}/register?ref=${fallbackCode}` : '');

  const safeReferredUsers = Array.isArray(referralData.referred_users)
    ? referralData.referred_users
    : [];

  const safeHistory = Array.isArray(referralData.referral_history)
    ? referralData.referral_history
    : [];

  const adminFeePercentage = 20;

  // ==================== HANDLERS ====================
  const handleCopyLink = () => {
    if (!referralLink) {
      toast.error('Referral link not available yet');
      return;
    }
    copyToClipboard(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 3000);
  };

  const shareOnSocial = (platform) => {
    if (!referralLink) {
      toast.error('Referral link not available yet');
      return;
    }

    const text = `🎯 Join me on XPunters — the ultimate fan-to-fan sports prediction marketplace! Use my referral link: ${referralLink}`;
    const url = encodeURIComponent(referralLink);
    const encodedText = encodeURIComponent(text);

    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${url}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${encodedText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${encodedText}`,
      email: `mailto:?subject=Join%20XPunters&body=${encodedText}%20${url}`,
    };

    const target = shareUrls[platform];
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
  };

  // ==================== LOADING ====================
  if (loading) {
    return (
      <div className="referral-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // ==================== RENDER ====================
  return (
    <div className="referral-dashboard">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="back-button"
      >
        <FaArrowLeft /> Back
      </button>

      {/* ============ HEADER ============ */}
      <div className="referral-header">
        <div className="header-content">
          <h1>
            <FaGift className="header-icon" /> Referral Program
          </h1>
          <p className="header-subtitle">
            Earn {adminFeePercentage}% of admin fees whenever your referrals win
          </p>
        </div>
      </div>

      {/* ============ STATS ============ */}
      <div className="stats-grid">
        <div className="stat-card">
          <FaUsers className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{referralData.total_referrals}</span>
            <span className="stat-label">Total Referrals</span>
          </div>
        </div>

        <div className="stat-card gold">
          <FaPercentage className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">{adminFeePercentage}%</span>
            <span className="stat-label">Admin Fee Share</span>
          </div>
        </div>

        <div className="stat-card green">
          <FaWallet className="stat-icon" />
          <div className="stat-info">
            <span className="stat-value">
              {formatXPUNT(referralData.referral_earnings)}
            </span>
            <span className="stat-label">Total Earnings</span>
          </div>
        </div>
      </div>

      {/* ============ HOW IT WORKS ============ */}
      <div className="how-it-works-card">
        <h3>💰 How Referral Earnings Work</h3>
        <div className="how-steps">
          <div className="how-step">
            <span className="step-num">1</span>
            <span>Share your referral link with friends</span>
          </div>
          <div className="how-step">
            <span className="step-num">2</span>
            <span>Friend joins and starts predicting</span>
          </div>
          <div className="how-step">
            <span className="step-num">3</span>
            <span>
              When they win, you earn {adminFeePercentage}% of admin fees
            </span>
          </div>
          <div className="how-step highlight">
            <span className="step-num">⚡</span>
            <span>Earnings are automatically credited to your wallet</span>
          </div>
        </div>
      </div>

      {/* ============ REFERRAL LINK ============ */}
      <div className="referral-link-section">
        <h3>Your Referral Link</h3>

        <div className="referral-link-box">
          <code className="referral-link">
            {referralLink || 'Generating your link…'}
          </code>
          <button
            type="button"
            onClick={handleCopyLink}
            className="copy-btn"
            disabled={!referralLink}
          >
            {copied ? <FaCheck /> : <FaCopy />}
          </button>
        </div>

        <p className="referral-hint">
          Share this link with your friends and earn {adminFeePercentage}% of
          admin fees when they win!
        </p>

        {/* ============ SHARE BUTTONS ============ */}
        <div className="share-buttons">
          <button
            type="button"
            onClick={() => shareOnSocial('twitter')}
            className="share-btn twitter"
            title="Share on Twitter"
            aria-label="Share on Twitter"
          >
            <FaTwitter />
          </button>
          <button
            type="button"
            onClick={() => shareOnSocial('whatsapp')}
            className="share-btn whatsapp"
            title="Share on WhatsApp"
            aria-label="Share on WhatsApp"
          >
            <FaWhatsapp />
          </button>
          <button
            type="button"
            onClick={() => shareOnSocial('telegram')}
            className="share-btn telegram"
            title="Share on Telegram"
            aria-label="Share on Telegram"
          >
            <FaTelegram />
          </button>
          <button
            type="button"
            onClick={() => shareOnSocial('facebook')}
            className="share-btn facebook"
            title="Share on Facebook"
            aria-label="Share on Facebook"
          >
            <FaFacebook />
          </button>
          <button
            type="button"
            onClick={() => shareOnSocial('email')}
            className="share-btn email"
            title="Share via Email"
            aria-label="Share via Email"
          >
            <FaEnvelope />
          </button>
        </div>
      </div>

      {/* ============ BENEFITS ============ */}
      <div className="benefits-section">
        <h3>🎯 Referral Benefits</h3>
        <div className="benefits-grid">
          <div className="benefit-item">
            <div className="benefit-icon">💰</div>
            <div className="benefit-info">
              <h4>{adminFeePercentage}% of Admin Fees</h4>
              <p>
                Earn {adminFeePercentage}% from every admin fee your referrals
                generate
              </p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">⚡</div>
            <div className="benefit-info">
              <h4>Automatic Payouts</h4>
              <p>No manual claims needed — earnings go straight to your wallet</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">📈</div>
            <div className="benefit-info">
              <h4>Unlimited Earnings</h4>
              <p>Refer as many friends as you want. No limits on earnings</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">🏆</div>
            <div className="benefit-info">
              <h4>Passive Income</h4>
              <p>Keep earning as your friends continue to win predictions</p>
            </div>
          </div>
        </div>
      </div>

      {/* ============ REFERRED USERS ============ */}
      <div className="referred-users-section">
        <h3>
          <FaUsers /> Referred Users
        </h3>

        {safeReferredUsers.length === 0 ? (
          <div className="empty-state">
            <p>No referrals yet. Share your link and start earning!</p>
          </div>
        ) : (
          <div className="users-table">
            <div className="table-header">
              <span>User</span>
              <span>Joined</span>
              <span>Status</span>
              <span>Earnings</span>
            </div>

            {safeReferredUsers.map((refUser, idx) => {
              const key = refUser.id ?? refUser.user_id ?? refUser.name ?? idx;
              const name = refUser.name || refUser.display_name || 'Unknown';
              const joined = refUser.joined || refUser.joined_at || refUser.date || null;
              const earnings = Number(refUser.earnings || 0);

              return (
                <div key={key} className="table-row">
                  <span className="user-name">
                    <span className="user-avatar">{name[0] || '?'}</span>
                    {name}
                  </span>
                  <span className="user-date">
                    {joined ? formatDate(joined) : '—'}
                  </span>
                  <span className="user-status active">✅ Active</span>
                  <span className="user-earnings">
                    {formatXPUNT(earnings)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ============ REFERRAL HISTORY ============ */}
      {safeHistory.length > 0 && (
        <div className="bonus-history-section">
          <h3>
            <FaHistory /> Referral Earnings History
          </h3>
          <div className="bonus-list">
            {safeHistory.map((item, idx) => {
              const key = item.id ?? item.tx_id ?? idx;
              const type =
                item.type === 'referral_earning'
                  ? '💰 Referral Earning'
                  : item.type || 'Earning';
              const date = item.created_at || item.date || null;
              const amount = Number(item.amount || 0);

              return (
                <div key={key} className="bonus-item">
                  <div className="bonus-item-info">
                    <span className="bonus-type">{type}</span>
                    {date && (
                      <span className="bonus-date">{formatDate(date)}</span>
                    )}
                    {item.description && (
                      <span className="bonus-description">{item.description}</span>
                    )}
                  </div>
                  <span className="bonus-amount positive">
                    +{formatXPUNT(amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;